import { useEffect, useState } from 'react';

// User-activity signals that reset the idle timer. We intentionally omit
// `mousemove`/`pointermove` — they fire constantly and a wall display with a
// drifting cursor would never be considered idle.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Returns `true` once there has been no user activity for `thresholdMs`.
 *
 * The timer resets on any activity event and whenever the tab becomes visible
 * again. A hidden tab keeps counting down (it can't receive activity events),
 * which is fine — hidden tabs are independently treated as safe to reload by
 * the auto-update controller.
 */
export function useIsIdle(thresholdMs: number): boolean {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    let timer: ReturnType<typeof setTimeout>;

    const reset = () => {
      // setState bails out when the value is unchanged, so spamming activity
      // events does not cause re-renders.
      setIsIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIsIdle(true), thresholdMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') reset();
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    reset(); // start the countdown

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, reset));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [thresholdMs]);

  return isIdle;
}
